/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Widen image-CDN URL helpers.
 *
 * Product imagery sourced from Widen (the `pdpMainImage` / `pdpAltImage1` product attributes) is
 * served from Widen's own CDN, not Salesforce DIS — so `<DynamicImage>` does not apply. Widen
 * resizes and recompresses server-side via query params instead: `?w=400` returns a genuinely
 * resized asset (~90% smaller than the full master), and `?q=` lowers quality further. We cap the
 * delivered size to the slot the image occupies rather than shipping the full-resolution master.
 *
 * @see widenImageUrl / widenImageSrcSet below.
 */

/**
 * Default width ladder (in CSS px) for product-tile images. PLP grid slots render roughly
 * 180–360 CSS px wide (2 columns on mobile → 4 on desktop); the upper widths cover high-DPR
 * displays. The browser picks the smallest candidate that satisfies the slot + device pixel ratio.
 */
export const DEFAULT_WIDEN_TILE_WIDTHS = [200, 300, 400, 600, 800] as const;

/** Width used for the plain `src` fallback (browsers that ignore `srcset`). A mid-ladder value. */
export const DEFAULT_WIDEN_FALLBACK_WIDTH = 600;

/**
 * Width for a video poster frame. A `poster` takes no `srcset`, so one width serves every viewport,
 * and it is sized to the widest slot a video occupies: `.section-container` is `max-w-content-full`
 * (1440px) with `px-6`, so the content box is 1440 − (2 × 24) = **1392**. Exact-fit rather than the
 * next round number down — 1392 measures 56 KB as WebP against 50 KB at 1280, which is a cheap 5.7 KB
 * for a poster that is otherwise upscaled ~9% on every full-width video.
 */
export const DEFAULT_WIDEN_POSTER_WIDTH = 1392;

export interface WidenImageOptions {
    /** Target width in px → Widen `w` query param (server-side resize). */
    w?: number;
    /** Quality 1–100 → Widen `q` query param. Omit to use Widen's default (~80). */
    q?: number;
}

/**
 * Build a Widen CDN image URL at a given width/quality.
 *
 * Any existing query string on the stored URL is stripped first — the DAM stores URLs that may
 * already carry params, and Widen would otherwise see a malformed `?a=b?w=400` — then our params
 * are applied. Mirrors the SFRA `ProductImageDAM` "strip then apply" behavior.
 *
 * @returns the transformed URL, or `''` when no URL is given.
 */
export function widenImageUrl(url: string | undefined | null, options: WidenImageOptions = {}): string {
    if (!url) return '';

    const [base] = url.split('?');
    const params = new URLSearchParams();
    if (options.w != null) params.set('w', String(options.w));
    if (options.q != null) params.set('q', String(options.q));

    const query = params.toString();
    return query ? `${base}?${query}` : base;
}

/**
 * Build a `srcset` string of width-descriptor candidates from a single Widen URL, so the browser
 * downloads exactly one slot- and DPR-appropriate image.
 *
 * @returns a `srcset` value (e.g. `"…?w=200 200w, …?w=300 300w"`), or `''` when no URL is given.
 */
export function widenImageSrcSet(
    url: string | undefined | null,
    widths: readonly number[] = DEFAULT_WIDEN_TILE_WIDTHS,
    options: Pick<WidenImageOptions, 'q'> = {}
): string {
    if (!url || widths.length === 0) return '';

    return widths.map((w) => `${widenImageUrl(url, { w, q: options.q })} ${w}w`).join(', ');
}

/**
 * Convert a Widen video *view* URL (`/view/video/<id>/<file>?u=…`) to its direct MP4 delivery URL
 * (`/content/<id>/mp4/<file>`). The `/view/` path is a chrome'd viewer page (served as `text/html`)
 * that a `<video>` element can't play; `/content/<id>/mp4/<file>` 303-redirects to the playable file
 * (same transform the Hero uses). Returns the input unchanged if it isn't a recognizable view URL.
 */
export function widenVideoUrl(link: string | undefined | null): string {
    if (!link) return '';
    try {
        const url = new URL(link);
        const match = url.pathname.match(/^\/view\/video\/([^/]+)\/(.+)$/);
        if (match) {
            url.pathname = `/content/${match[1]}/mp4/${match[2]}`;
        }
        return url.toString();
    } catch {
        return link;
    }
}

/**
 * Derive a poster frame for a Widen video from its MP4 delivery URL (the output of
 * {@link widenVideoUrl} / `widenVideoFileUrl`). Widen's delivery path is
 * `/content/<id>/<format>/<file>` — swapping the `mp4` format segment for `webp` returns a still
 * frame of the same asset, so no separate thumbnail has to be authored (UCR-3046). The file
 * extension is ignored by Widen; only the format segment matters.
 *
 * A poster matters most on iOS: WebKit does not paint the first frame from `preload="metadata"` the
 * way desktop Chrome/Firefox do, so a poster-less `<video>` shows an empty box until the user plays it.
 *
 * `options` is merged over {@link DEFAULT_WIDEN_POSTER_WIDTH}, not substituted for it, so tuning one
 * knob (`{ q: 60 }`) can't silently drop the width and ship the full-resolution master. Pass
 * `{ w: undefined }` to genuinely opt out of the resize.
 *
 * @returns the poster URL, or `''` when no frame can be derived — a non-Widen host (only
 * `*.widen.net` serves these renditions; `embed.widencdn.net`, for one, answers `/content/` with a
 * 404 placeholder image that would paint as a broken poster) or a URL that isn't an MP4 delivery URL.
 */
export function widenVideoPosterUrl(videoUrl: string | undefined | null, options: WidenImageOptions = {}): string {
    if (!videoUrl) return '';
    try {
        const url = new URL(videoUrl);
        if (url.hostname !== 'widen.net' && !url.hostname.endsWith('.widen.net')) return '';
        const match = url.pathname.match(/^\/content\/([^/]+)\/mp4\/(.+)$/);
        if (!match) return '';
        url.pathname = `/content/${match[1]}/webp/${match[2]}`;
        // widenImageUrl strips the stored query (Widen's `u=` tracking param) before applying ours.
        return widenImageUrl(url.toString(), { w: DEFAULT_WIDEN_POSTER_WIDTH, ...options });
    } catch {
        return '';
    }
}
